import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingCart, User, Phone, Menu, X, ChevronDown } from "lucide-react";
import { CATEGORIES, searchProducts, fmt } from "../data/store";
import { useCart } from "../context/CartContext";

const Logo = () => (
  <Link to="/" data-testid="header-logo" className="flex flex-col leading-none shrink-0">
    <span className="text-2xl font-extrabold tracking-tight text-[#111111]">E-Commerce <span className="text-[#D0121A]">DEMO</span></span>
    <span className="text-[10px] text-[#6B6B6B] tracking-wide hidden sm:block">Interneta veikala demonstrācija · vairumā un mazumā</span>
  </Link>
);

const SearchBar = () => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);
  const results = q.trim().length >= 2 ? searchProducts(q).slice(0, 5) : [];
  const catHits = q.trim().length >= 2 ? CATEGORIES.filter((c) => c.name.toLowerCase().includes(q.toLowerCase())).slice(0, 3) : [];

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) { navigate(`/mekle?q=${encodeURIComponent(q.trim())}`); setOpen(false); }
  };

  return (
    <div ref={ref} className="relative flex-1 max-w-2xl">
      <form onSubmit={submit}>
        <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
          data-testid="search-input" placeholder="Meklēt preces, zīmolus, kategorijas…"
          className="w-full h-11 pl-4 pr-12 rounded-md border border-[#E2E2E2] bg-[#F5F5F5] text-sm focus:outline-none focus:ring-2 focus:ring-[#111111] focus:bg-white transition-colors" />
        <button type="submit" data-testid="search-submit" aria-label="Meklēt"
          className="absolute right-1 top-1 h-9 w-10 flex items-center justify-center bg-[#111111] hover:bg-[#3A3A3A] text-white rounded-md transition-colors">
          <Search size={18} />
        </button>
      </form>
      {open && (results.length > 0 || catHits.length > 0) && (
        <div data-testid="search-autocomplete" className="absolute top-12 left-0 right-0 bg-white border border-[#E2E2E2] rounded-md shadow-xl z-50 overflow-hidden">
          {catHits.length > 0 && (
            <div className="p-2 border-b border-[#E2E2E2]">
              <span className="px-2 text-[11px] uppercase tracking-widest text-[#6B6B6B] font-bold">Kategorijas</span>
              {catHits.map((c) => (
                <Link key={c.slug} to={`/kategorija/${c.slug}`} onClick={() => setOpen(false)}
                  className="block px-2 py-1.5 text-sm hover:bg-[#F5F5F5] rounded">{c.name}</Link>
              ))}
            </div>
          )}
          {results.map((r) => (
            <Link key={r.id} to={`/prece/${r.id}`} onClick={() => setOpen(false)} data-testid={`autocomplete-item-${r.id}`}
              className="flex items-center gap-3 px-3 py-2 hover:bg-[#F5F5F5] transition-colors">
              <img src={r.image} alt="" className="w-10 h-10 object-cover rounded bg-[#F5F5F5]" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{r.name}</div>
                <div className="text-xs text-[#6B6B6B]">{r.brand}</div>
              </div>
              <span className="text-sm font-bold">{fmt(r.price)}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const MegaMenu = () => {
  const [openCat, setOpenCat] = useState(null);
  return (
    <nav className="hidden lg:block border-t border-[#E2E2E2] bg-white" onMouseLeave={() => setOpenCat(null)}>
      <div className="max-w-7xl mx-auto px-4 flex items-center">
        {CATEGORIES.map((c) => (
          <div key={c.slug} onMouseEnter={() => setOpenCat(c.slug)} className="relative">
            <Link to={`/kategorija/${c.slug}`} data-testid={`nav-cat-${c.slug}`}
              className={`flex items-center gap-1 px-3 py-3 text-[13px] font-semibold whitespace-nowrap transition-colors border-b-2 ${openCat === c.slug ? "text-[#111111] border-[#111111]" : "text-[#111111] border-transparent hover:text-[#111111]"}`}>
              {c.name.split(" un ")[0]}
              <ChevronDown size={13} />
            </Link>
          </div>
        ))}
        <Link to="/akcijas" data-testid="nav-akcijas" className="px-3 py-3 text-[13px] font-bold text-[#D0121A] whitespace-nowrap hover:underline">Akcijas</Link>
        <Link to="/jaunumi" data-testid="nav-jaunumi" className="px-3 py-3 text-[13px] font-bold text-[#111111] whitespace-nowrap hover:underline">Jaunumi</Link>
      </div>
      {openCat && (
        <div data-testid="megamenu-panel" className="absolute left-0 right-0 bg-white border-t border-b border-[#E2E2E2] shadow-xl z-40">
          <div className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-4 gap-6">
            {(() => {
              const c = CATEGORIES.find((x) => x.slug === openCat);
              return (
                <>
                  <div className="col-span-3 grid grid-cols-3 gap-x-6 gap-y-1">
                    <div className="col-span-3 mb-2">
                      <Link to={`/kategorija/${c.slug}`} onClick={() => setOpenCat(null)} className="text-sm font-extrabold text-[#111111] hover:underline">
                        Visas: {c.name} →
                      </Link>
                    </div>
                    {c.subs.map((s) => (
                      <Link key={s} to={`/kategorija/${c.slug}?sub=${encodeURIComponent(s)}`} onClick={() => setOpenCat(null)}
                        className="text-sm text-[#111111] py-1.5 hover:text-[#111111] hover:underline">{s}</Link>
                    ))}
                  </div>
                  <div className="bg-[#F5F5F5] rounded-md p-4 flex flex-col justify-end">
                    <p className="text-sm font-bold text-[#111111]">{c.name}</p>
                    <p className="text-xs text-[#6B6B6B] mt-1">{c.desc}</p>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </nav>
  );
};

const MobileMenu = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute left-0 top-0 bottom-0 w-80 max-w-[85vw] bg-white overflow-y-auto p-4">
        <div className="flex justify-between items-center mb-4">
          <Logo />
          <button onClick={onClose} data-testid="mobile-menu-close" aria-label="Aizvērt"><X size={22} /></button>
        </div>
        {CATEGORIES.map((c) => (
          <div key={c.slug} className="border-b border-[#E2E2E2] py-2">
            <Link to={`/kategorija/${c.slug}`} onClick={onClose} className="block font-semibold text-sm py-1">{c.name}</Link>
          </div>
        ))}
        <div className="pt-3 flex flex-col gap-2 text-sm">
          <Link to="/akcijas" onClick={onClose} className="font-bold text-[#D0121A]">Akcijas</Link>
          <Link to="/jaunumi" onClick={onClose} className="font-bold text-[#111111]">Jaunumi</Link>
          <Link to="/zimoli" onClick={onClose}>Zīmoli</Link>
          <Link to="/vairumtirdznieciba" onClick={onClose}>Vairumtirdzniecība</Link>
          <Link to="/par-mums" onClick={onClose}>Par mums / Kontakti</Link>
        </div>
      </div>
    </div>
  );
};

export const Header = () => {
  const { count, setDrawerOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm">
      <div className="bg-[#111111] text-white text-xs">
        <div className="max-w-7xl mx-auto px-4 py-1.5 flex items-center justify-between">
          <span className="hidden sm:block">Bezmaksas piegāde pasūtījumiem virs 60 € · Demo veikals</span>
          <div className="flex items-center gap-4 ml-auto">
            <a href="tel:+37120000000" data-testid="header-phone" className="flex items-center gap-1 hover:underline"><Phone size={12} /> +371 20 000 000</a>
            <Link to="/vairumtirdznieciba" data-testid="header-wholesale-link" className="font-semibold hover:underline">Vairumtirdzniecība</Link>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
        <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-open" aria-label="Izvēlne"><Menu size={24} /></button>
        <Logo />
        <div className="hidden md:flex flex-1 justify-center"><SearchBar /></div>
        <div className="flex items-center gap-2 ml-auto">
          <Link to="/konts" data-testid="header-account-btn" className="hidden sm:flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-[#111111] hover:text-[#D0121A] transition-colors">
            <User size={20} /> <span className="hidden xl:inline">Konts</span>
          </Link>
          <button onClick={() => setDrawerOpen(true)} data-testid="header-cart-btn" aria-label="Grozs"
            className="relative flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-[#111111] hover:text-[#111111] transition-colors">
            <ShoppingCart size={22} />
            {count > 0 && <span data-testid="cart-count-badge" className="absolute -top-1 -right-0 bg-[#D0121A] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{count}</span>}
            <span className="hidden xl:inline">Grozs</span>
          </button>
        </div>
      </div>
      <div className="md:hidden px-4 pb-3"><SearchBar /></div>
      <MegaMenu />
      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </header>
  );
};
